import React, { useState, useEffect, useCallback } from 'react';
import { DashboardLayout } from '../components/DashboardLayout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { MessageSquare, TrendingUp, Users, IndianRupee, ArrowUpRight, ArrowDownRight, Phone, CheckCircle, XCircle, Clock, BarChart3, Activity, Send, Megaphone, Loader2 } from 'lucide-react';

import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';

import { BACKEND_URL, API } from '../utils/apiConfig';

function MetricCard({ icon: Icon, label, value, subtext, color = 'text-primary', bgColor = 'bg-primary/10' }) {
  return (
    <Card data-testid={`metric-${label.toLowerCase().replace(/\s+/g, '-')}`} className="p-4">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">{label}</p>
          <p className="text-2xl font-bold text-slate-900 mt-1">{value}</p>
          {subtext && <p className="text-xs text-slate-500 mt-1">{subtext}</p>}
        </div>
        <div className={`w-10 h-10 rounded-lg ${bgColor} flex items-center justify-center`}>
          <Icon className={`w-5 h-5 ${color}`} />
        </div>
      </div>
    </Card>
  );
}

function BarChartSimple({ data, labelKey, valueKey, maxValue, color = '#4f46e5' }) {
  if (!data || data.length === 0) return <p className="text-sm text-slate-400 text-center py-4">No data</p>;
  const max = maxValue || Math.max(...data.map(d => d[valueKey] || 0), 1);

  return (
    <div className="space-y-2">
      {data.map((item, i) => (
        <div key={i} className="flex items-center gap-3">
          <span className="text-xs text-slate-600 w-24 shrink-0 text-right truncate">{item[labelKey]}</span>
          <div className="flex-1 bg-slate-100 rounded-full h-5 overflow-hidden">
            <div
              className="h-full rounded-full transition-all duration-500"
              style={{ width: `${Math.max((item[valueKey] / max) * 100, 2)}%`, backgroundColor: color }}
            />
          </div>
          <span className="text-xs font-semibold text-slate-700 w-8">{item[valueKey]}</span>
        </div>
      ))}
    </div>
  );
}

function ConversationStatusBadge({ status }) {
  const styles = {
    completed: 'bg-green-100 text-green-800',
    active: 'bg-blue-100 text-blue-800',
    cancelled: 'bg-red-100 text-red-800',
    expired: 'bg-slate-100 text-slate-600',
  };
  return <Badge className={`text-[10px] ${styles[status] || styles.expired}`}>{status}</Badge>;
}

function DailyTrendChart({ trend }) {
  if (!trend || trend.length === 0) return null;
  const maxConv = Math.max(...trend.map(d => d.conversations), 1);
  const maxRev = Math.max(...trend.map(d => d.revenue), 1);
  const chartH = 120;
  const barW = Math.min(Math.max(Math.floor(680 / trend.length) - 2, 4), 20);

  return (
    <div className="overflow-x-auto">
      <div className="flex items-end gap-0.5 min-w-[600px]" style={{ height: chartH }}>
        {trend.map((d, i) => {
          const convH = (d.conversations / maxConv) * chartH;
          const isWeekend = new Date(d.date).getDay() === 0 || new Date(d.date).getDay() === 6;
          return (
            <div key={i} className="flex flex-col items-center" style={{ width: barW + 2 }} title={`${d.date}\nConv: ${d.conversations}\nBookings: ${d.bookings}\nRev: ₹${d.revenue}`}>
              <div
                className={`rounded-t transition-all duration-300 ${isWeekend ? 'bg-amber-400' : 'bg-primary'}`}
                style={{ width: barW, height: Math.max(convH, 2) }}
              />
            </div>
          );
        })}
      </div>
      <div className="flex items-center gap-0.5 min-w-[600px] mt-1 border-t border-slate-200 pt-1">
        {trend.map((d, i) => {
          const show = i === 0 || i === trend.length - 1 || i % Math.max(Math.floor(trend.length / 6), 1) === 0;
          return (
            <div key={i} className="text-center" style={{ width: barW + 2 }}>
              {show && <span className="text-[9px] text-slate-400">{d.date.slice(5)}</span>}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function WhatsAppAnalyticsPage() {
  const { token } = useAuth();
  const [days, setDays] = useState('30');
  const [overview, setOverview] = useState(null);
  const [trend, setTrend] = useState([]);
  const [popularTimes, setPopularTimes] = useState(null);
  const [recentConvs, setRecentConvs] = useState([]);
  const [msgStats, setMsgStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [broadcastTargets, setBroadcastTargets] = useState([]);
  const [selectedTargets, setSelectedTargets] = useState([]);
  const [broadcastMode, setBroadcastMode] = useState('template');
  const [templateName, setTemplateName] = useState('');
  const [templateParams, setTemplateParams] = useState('');
  const [broadcastText, setBroadcastText] = useState('');
  const [sending, setSending] = useState(false);
  const [broadcastHistory, setBroadcastHistory] = useState([]);
  const [showBroadcast, setShowBroadcast] = useState(false);
  const [filterGame, setFilterGame] = useState('all');
  const [filterVenue, setFilterVenue] = useState('all');

  const headers = { Authorization: `Bearer ${token}` };

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [ovRes, trRes, ptRes, rcRes, msRes] = await Promise.all([
        axios.get(`${API}/whatsapp-analytics/overview?days=${days}`, { headers }),
        axios.get(`${API}/whatsapp-analytics/daily-trend?days=${days}`, { headers }),
        axios.get(`${API}/whatsapp-analytics/popular-times?days=${days}`, { headers }),
        axios.get(`${API}/whatsapp-analytics/recent-conversations?limit=15`, { headers }),
        axios.get(`${API}/whatsapp-analytics/message-stats?days=7`, { headers }),
      ]);
      setOverview(ovRes.data);
      setTrend(trRes.data.trend || []);
      setPopularTimes(ptRes.data);
      setRecentConvs(rcRes.data.conversations || []);
      setMsgStats(msRes.data);
    } catch (err) {
      console.error('Analytics fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, [days, token]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const loadBroadcastData = async () => {
    try {
      const [tRes, hRes] = await Promise.all([
        axios.get(`${API}/whatsapp-analytics/broadcast/targets?days=90`, { headers }),
        axios.get(`${API}/whatsapp-analytics/broadcast/history?limit=10`, { headers }),
      ]);
      setBroadcastTargets(tRes.data.targets || []);
      setBroadcastHistory(hRes.data.broadcasts || []);
    } catch (err) {
      console.error('Broadcast data error:', err);
    }
  };

  const handleToggleBroadcast = () => {
    if (!showBroadcast) loadBroadcastData();
    setShowBroadcast(!showBroadcast);
  };

  const filteredTargets = broadcastTargets.filter(t => {
    if (filterGame !== 'all' && t.favorite_game !== filterGame) return false;
    if (filterVenue !== 'all' && t.favorite_venue !== filterVenue) return false;
    return true;
  });

  const uniqueGames = [...new Set(broadcastTargets.map(t => t.favorite_game).filter(Boolean))];
  const uniqueVenues = [...new Set(broadcastTargets.map(t => t.favorite_venue).filter(Boolean))];

  const toggleTarget = (mobile) => {
    setSelectedTargets(prev =>
      prev.includes(mobile) ? prev.filter(m => m !== mobile) : [...prev, mobile]
    );
  };

  const selectAllFiltered = () => {
    const mobiles = filteredTargets.map(t => t.mobile);
    setSelectedTargets(prev => {
      const existing = new Set(prev);
      const allSelected = mobiles.every(m => existing.has(m));
      if (allSelected) return prev.filter(m => !mobiles.includes(m));
      return [...new Set([...prev, ...mobiles])];
    });
  };

  const sendBroadcast = async () => {
    if (selectedTargets.length === 0) { toast.error('No targets selected'); return; }
    if (broadcastMode === 'template' && !templateName.trim()) { toast.error('Template name is required'); return; }
    if (broadcastMode === 'text' && !broadcastText.trim()) { toast.error('Message text is required'); return; }

    setSending(true);
    try {
      const res = await axios.post(`${API}/whatsapp-analytics/broadcast/send`, {
        template_name: templateName,
        template_params: templateParams,
        message_text: broadcastText,
        mobiles: selectedTargets,
        use_template: broadcastMode === 'template',
      }, { headers });

      toast.success(`Broadcast sent: ${res.data.sent} delivered, ${res.data.failed} failed`);
      setSelectedTargets([]);
      loadBroadcastData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Broadcast failed');
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full" />
        </div>
      </DashboardLayout>
    );
  }

  const conv = overview?.conversations || {};
  const bk = overview?.bookings || {};
  const msg = overview?.messages || {};

  return (
    <DashboardLayout>
      <div data-testid="whatsapp-analytics-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <MessageSquare className="w-6 h-6 text-green-600" /> WhatsApp Analytics
            </h1>
            <p className="text-sm text-slate-500 mt-1">Conversation metrics, booking conversions & message performance</p>
          </div>
          <Select value={days} onValueChange={setDays}>
            <SelectTrigger data-testid="wa-analytics-period" className="w-36">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7">Last 7 days</SelectItem>
              <SelectItem value="14">Last 14 days</SelectItem>
              <SelectItem value="30">Last 30 days</SelectItem>
              <SelectItem value="90">Last 90 days</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Top Metrics */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
          <MetricCard icon={Users} label="Conversations" value={conv.total || 0} subtext={`${conv.completed || 0} completed`} color="text-blue-600" bgColor="bg-blue-50" />
          <MetricCard icon={TrendingUp} label="Conversion Rate" value={`${bk.conversion_rate || 0}%`} subtext={`${bk.total || 0} bookings created`} color="text-green-600" bgColor="bg-green-50" />
          <MetricCard icon={IndianRupee} label="WA Revenue" value={`₹${(overview?.revenue || 0).toLocaleString()}`} subtext={`${bk.paid || 0} paid bookings`} color="text-amber-600" bgColor="bg-amber-50" />
          <MetricCard icon={Phone} label="Messages" value={msg.total_received || 0} subtext={`${msg.replies_sent || 0} replied`} color="text-purple-600" bgColor="bg-purple-50" />
        </div>

        {/* Conversation Funnel */}
        <div className="grid lg:grid-cols-5 gap-3 mb-6">
          {[
            { label: 'Total', value: conv.total, icon: MessageSquare, color: 'text-slate-600', bg: 'bg-slate-50' },
            { label: 'Active', value: conv.active, icon: Activity, color: 'text-blue-600', bg: 'bg-blue-50' },
            { label: 'Completed', value: conv.completed, icon: CheckCircle, color: 'text-green-600', bg: 'bg-green-50' },
            { label: 'Cancelled', value: conv.cancelled, icon: XCircle, color: 'text-red-600', bg: 'bg-red-50' },
            { label: 'Expired', value: conv.expired, icon: Clock, color: 'text-slate-400', bg: 'bg-slate-50' },
          ].map(({ label, value, icon: Ic, color, bg }) => (
            <Card key={label} className="p-3 text-center">
              <div className={`w-8 h-8 rounded-full ${bg} flex items-center justify-center mx-auto mb-1`}>
                <Ic className={`w-4 h-4 ${color}`} />
              </div>
              <p className="text-lg font-bold text-slate-900">{value || 0}</p>
              <p className="text-[10px] text-slate-500 uppercase font-medium">{label}</p>
            </Card>
          ))}
        </div>

        {/* Daily Trend */}
        <Card className="p-5 mb-6">
          <h2 className="text-sm font-semibold text-slate-700 mb-4 flex items-center gap-2">
            <BarChart3 className="w-4 h-4 text-slate-400" /> Daily Conversation Trend
          </h2>
          <DailyTrendChart trend={trend} />
          <div className="flex items-center gap-4 mt-2 text-[10px] text-slate-500">
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-primary inline-block" /> Weekday</span>
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-amber-400 inline-block" /> Weekend</span>
          </div>
        </Card>

        {/* Breakdowns */}
        <div className="grid lg:grid-cols-2 gap-5 mb-6">
          {/* Popular Hours */}
          <Card className="p-5">
            <h2 className="text-sm font-semibold text-slate-700 mb-4">Popular Booking Hours</h2>
            <BarChartSimple data={popularTimes?.by_hour || []} labelKey="hour" valueKey="count" color="#4f46e5" />
          </Card>

          {/* Day of Week */}
          <Card className="p-5">
            <h2 className="text-sm font-semibold text-slate-700 mb-4">Bookings by Day</h2>
            <BarChartSimple data={popularTimes?.by_day || []} labelKey="day" valueKey="count" color="#f59e0b" />
          </Card>

          {/* By Game */}
          <Card className="p-5">
            <h2 className="text-sm font-semibold text-slate-700 mb-4">Bookings by Sport</h2>
            <BarChartSimple data={popularTimes?.by_game || []} labelKey="game" valueKey="count" color="#10b981" />
          </Card>

          {/* By Venue */}
          <Card className="p-5">
            <h2 className="text-sm font-semibold text-slate-700 mb-4">Bookings by Venue</h2>
            <BarChartSimple data={popularTimes?.by_venue || []} labelKey="venue" valueKey="count" color="#8b5cf6" />
          </Card>
        </div>

        {/* Message Volume (Hourly) */}
        {msgStats && (
          <Card className="p-5 mb-6">
            <h2 className="text-sm font-semibold text-slate-700 mb-4">Message Volume by Hour (Last 7 days)</h2>
            <div className="overflow-x-auto">
              <div className="flex items-end gap-1 min-w-[500px]" style={{ height: 80 }}>
                {msgStats.hourly?.map((h, i) => {
                  const max = Math.max(...msgStats.hourly.map(x => x.received), 1);
                  const barH = (h.received / max) * 80;
                  return (
                    <div key={i} className="flex flex-col items-center flex-1" title={`${h.hour}:00 - Received: ${h.received}, Replied: ${h.replied}`}>
                      <div className="bg-green-400 rounded-t w-full transition-all duration-300" style={{ height: Math.max(barH, 1) }} />
                    </div>
                  );
                })}
              </div>
              <div className="flex gap-1 min-w-[500px] mt-1 border-t border-slate-200 pt-1">
                {msgStats.hourly?.map((h, i) => (
                  <div key={i} className="flex-1 text-center">
                    {i % 3 === 0 && <span className="text-[9px] text-slate-400">{h.hour}h</span>}
                  </div>
                ))}
              </div>
            </div>
          </Card>
        )}

        {/* Recent Conversations */}
        <Card className="p-5 mb-6">
          <h2 className="text-sm font-semibold text-slate-700 mb-4">Recent Conversations</h2>
          {recentConvs.length === 0 ? (
            <p className="text-sm text-slate-400 text-center py-6">No conversations yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="wa-recent-conversations-table">
                <thead>
                  <tr className="text-left text-xs text-slate-500 border-b border-slate-100">
                    <th className="pb-2 pr-3">Mobile</th>
                    <th className="pb-2 pr-3">Name</th>
                    <th className="pb-2 pr-3">Status</th>
                    <th className="pb-2 pr-3">Game</th>
                    <th className="pb-2 pr-3">Venue</th>
                    <th className="pb-2 pr-3">Date</th>
                    <th className="pb-2 pr-3">Time</th>
                    <th className="pb-2 pr-3">Msgs</th>
                    <th className="pb-2">Updated (IST)</th>
                  </tr>
                </thead>
                <tbody>
                  {recentConvs.map(c => (
                    <tr key={c.id} className="border-b border-slate-50 hover:bg-slate-50/50">
                      <td className="py-2 pr-3 font-medium text-slate-700">{c.mobile?.slice(-10) || '-'}</td>
                      <td className="py-2 pr-3 text-slate-700">{c.player_name || '-'}</td>
                      <td className="py-2 pr-3"><ConversationStatusBadge status={c.status} /></td>
                      <td className="py-2 pr-3 text-slate-600">{c.game || '-'}</td>
                      <td className="py-2 pr-3 text-slate-600 max-w-[120px] truncate">{c.venue || '-'}</td>
                      <td className="py-2 pr-3 text-slate-600">{c.date || '-'}</td>
                      <td className="py-2 pr-3 text-slate-600">{c.start_time || '-'}</td>
                      <td className="py-2 pr-3 text-slate-600">{c.message_count}</td>
                      <td className="py-2 text-slate-400 text-xs">{(() => {
                        const ts = c.updated_at;
                        if (!ts) return '-';
                        try {
                          const d = new Date(ts.endsWith('Z') ? ts : ts + 'Z');
                          const ist = new Date(d.getTime() + 5.5 * 60 * 60 * 1000);
                          return ist.toISOString().slice(0, 16).replace('T', ' ') + ' IST';
                        } catch { return ts.slice(0, 16).replace('T', ' '); }
                      })()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        {/* Broadcast Section */}
        <Card className="p-5" data-testid="wa-broadcast-section">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
              <Megaphone className="w-4 h-4 text-green-600" /> WhatsApp Broadcast
            </h2>
            <Button variant={showBroadcast ? 'default' : 'outline'} size="sm" data-testid="toggle-broadcast-btn" onClick={handleToggleBroadcast}>
              {showBroadcast ? 'Close' : 'Open Broadcast Panel'}
            </Button>
          </div>

          {showBroadcast && (
            <div className="space-y-5">
              {/* Target Filters */}
              <div className="flex flex-wrap gap-3 items-end">
                <div>
                  <Label className="text-xs text-slate-500">Filter by Sport</Label>
                  <Select value={filterGame} onValueChange={setFilterGame}>
                    <SelectTrigger data-testid="broadcast-filter-game" className="w-40"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Sports</SelectItem>
                      {uniqueGames.map(g => <SelectItem key={g} value={g}>{g}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Filter by Venue</Label>
                  <Select value={filterVenue} onValueChange={setFilterVenue}>
                    <SelectTrigger data-testid="broadcast-filter-venue" className="w-48"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Venues</SelectItem>
                      {uniqueVenues.map(v => <SelectItem key={v} value={v}>{v}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <Button variant="outline" size="sm" data-testid="broadcast-select-all" onClick={selectAllFiltered}>
                  {filteredTargets.length > 0 && filteredTargets.every(t => selectedTargets.includes(t.mobile)) ? 'Deselect All' : `Select All (${filteredTargets.length})`}
                </Button>
                <Badge className="bg-green-100 text-green-800">{selectedTargets.length} selected</Badge>
              </div>

              {/* Target List */}
              <div className="max-h-60 overflow-y-auto border rounded-lg">
                <table className="w-full text-sm">
                  <thead className="sticky top-0 bg-slate-50">
                    <tr className="text-left text-xs text-slate-500 border-b">
                      <th className="p-2 w-8"></th>
                      <th className="p-2">Mobile</th>
                      <th className="p-2">Name</th>
                      <th className="p-2">Bookings</th>
                      <th className="p-2">Fav Sport</th>
                      <th className="p-2">Fav Venue</th>
                      <th className="p-2">Fav Time</th>
                      <th className="p-2">Fav Day</th>
                      <th className="p-2">Last Booking</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredTargets.map(t => (
                      <tr key={t.mobile} className={`border-b border-slate-50 cursor-pointer hover:bg-green-50/50 ${selectedTargets.includes(t.mobile) ? 'bg-green-50' : ''}`} onClick={() => toggleTarget(t.mobile)}>
                        <td className="p-2">
                          <input type="checkbox" checked={selectedTargets.includes(t.mobile)} readOnly className="rounded" />
                        </td>
                        <td className="p-2 font-medium text-slate-700">{t.mobile}</td>
                        <td className="p-2 text-slate-600">{t.name || '-'}</td>
                        <td className="p-2 text-slate-600">{t.total_bookings}</td>
                        <td className="p-2"><Badge className="text-[10px] bg-blue-50 text-blue-700">{t.favorite_game || '-'}</Badge></td>
                        <td className="p-2 text-slate-600 max-w-[120px] truncate">{t.favorite_venue || '-'}</td>
                        <td className="p-2 text-slate-600">{t.favorite_hour || '-'}</td>
                        <td className="p-2 text-slate-600">{t.favorite_day || '-'}</td>
                        <td className="p-2 text-slate-400 text-xs">{t.last_booking || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {filteredTargets.length === 0 && <p className="text-center py-4 text-sm text-slate-400">No matching customers</p>}
              </div>

              {/* Message Compose */}
              <div className="border rounded-lg p-4 bg-slate-50/50 space-y-3">
                <div className="flex gap-3">
                  <Button
                    variant={broadcastMode === 'template' ? 'default' : 'outline'} size="sm"
                    data-testid="broadcast-mode-template"
                    onClick={() => setBroadcastMode('template')}
                  >WABA Template</Button>
                  <Button
                    variant={broadcastMode === 'text' ? 'default' : 'outline'} size="sm"
                    data-testid="broadcast-mode-text"
                    onClick={() => setBroadcastMode('text')}
                  >Text Message</Button>
                </div>

                {broadcastMode === 'template' ? (
                  <div className="space-y-2">
                    <div>
                      <Label>Template Name *</Label>
                      <Input data-testid="broadcast-template-name" value={templateName} onChange={e => setTemplateName(e.target.value)} placeholder="e.g. maks_promo_offer" />
                    </div>
                    <div>
                      <Label>Template Parameters (comma-separated)</Label>
                      <Input data-testid="broadcast-template-params" value={templateParams} onChange={e => setTemplateParams(e.target.value)} placeholder="e.g. Rahul,Pickleball,Saturday 6 PM" />
                      <p className="text-[10px] text-slate-400 mt-1">Values for template placeholders: {`{{1}}, {{2}}, {{3}}`}</p>
                    </div>
                  </div>
                ) : (
                  <div>
                    <Label>Message Text *</Label>
                    <Textarea data-testid="broadcast-text-input" value={broadcastText} onChange={e => setBroadcastText(e.target.value)} placeholder="Type your promotional message..." rows={3} />
                    <p className="text-[10px] text-slate-400 mt-1">Text messages only work within 24h session window</p>
                  </div>
                )}

                <Button
                  data-testid="broadcast-send-btn"
                  onClick={sendBroadcast}
                  disabled={sending || selectedTargets.length === 0}
                  className="bg-green-600 hover:bg-green-700"
                >
                  {sending ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Sending...</> : <><Send className="w-4 h-4 mr-1" /> Send to {selectedTargets.length} customers</>}
                </Button>
              </div>

              {/* Broadcast History */}
              {broadcastHistory.length > 0 && (
                <div>
                  <h3 className="text-xs font-semibold text-slate-500 uppercase mb-2">Broadcast History</h3>
                  <div className="space-y-2">
                    {broadcastHistory.map((b, i) => (
                      <div key={i} className="flex items-center justify-between bg-white border rounded-lg p-3 text-sm">
                        <div>
                          <span className="font-medium text-slate-700">{b.template_name || 'Text Message'}</span>
                          <span className="text-slate-400 text-xs ml-2">{b.created_at?.slice(0, 16).replace('T', ' ')}</span>
                        </div>
                        <div className="flex items-center gap-3">
                          <Badge className="bg-green-100 text-green-800 text-[10px]">{b.sent} sent</Badge>
                          {b.failed > 0 && <Badge className="bg-red-100 text-red-800 text-[10px]">{b.failed} failed</Badge>}
                          <span className="text-xs text-slate-400">/ {b.target_count} targets</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </Card>
      </div>
    </DashboardLayout>
  );
}
